import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineDislike, AiOutlineLike } from 'react-icons/ai';
import { AiFillFileExcel, AiFillFileImage, AiFillFilePdf, AiFillFileWord, AiFillFileZip } from 'react-icons/ai';
import { FaFileAudio, FaFileCsv, FaFileDownload, FaFilePowerpoint, FaFileVideo } from 'react-icons/fa';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import { FaTimes } from 'react-icons/fa';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Placeholder, Quiz, Text, Video, Audio, CheckpointRewardModal } from 'components';
import { useAuth, useBasic, useCourses, useModal } from 'contexts';
import { actionLog, getBreadcrumb, getDetailContent, getLessonLikes, registerFolderLog, getBadgeById } from 'services';
import { BreadcrumbInterface, DetailContentInterface, File, LessonLikesInterface, LessonLikesPlayerObj, BadgeInfo } from 'types';
import { findProgressByID, formatBytes, getAllLessonsFlat, detectCheckpointJump } from 'utils';
import { NextLessons } from './NextLessons';
import { LessonNavigation } from './LessonNavigation';

import './style.scss';
import { GameContainer, GameFolder } from 'components/GameContainer';

export default function LessonPage() {
  const { user } = useAuth();
  const { setShowHeader, rewardsConfig, courseBadgeMappings } = useBasic();
  const { allProgress, getFullProgress } = useCourses();
  const { courseID, folderID, contentID } = useParams();
  const navigate = useNavigate();
  const { showModal } = useModal();

  const [loading, setLoading] = useState(true);
  const [lessonContent, setLessonContent] = useState<DetailContentInterface[]>();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbInterface[]>();
  const [loadingLikes, setLoadingLikes] = useState(true);
  const [likes, setLikes] = useState<LessonLikesInterface | null | undefined>();
  const [prevFolderID, setPrevFolderID] = useState<string | undefined>(folderID);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const prevCoursePercent = useRef<number | null>(null);
  const isInitializedRef = useRef(false);
  const showModalRef = useRef(showModal);

  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  const coursePercent = useMemo(() => {
    if (!courseID || !Array.isArray(allProgress)) return 0;
    const cp = allProgress.find(p => p._id === courseID);
    return cp?.percent ?? 0;
  }, [allProgress, courseID]);

  useEffect(() => {
    if (!user?._id || !courseID) return;

    const prev = prevCoursePercent.current;
    const curr = coursePercent;

    // Inicializa o valor anterior na primeira execução
    if (!isInitializedRef.current) {
      prevCoursePercent.current = curr;
      isInitializedRef.current = true;
      return;
    }

    // Só executa se houve mudança real no progresso
    if (prev !== curr) {
      const checkpoints = detectCheckpointJump(prev, curr);
      
      if (checkpoints.length > 0) {
        (async () => {
          for (const cp of checkpoints) {
            try {

              const mapping = courseBadgeMappings.find(m => m.courseId === courseID);
              const is100 = cp === 100;
              const badgeId = mapping ? (is100 ? mapping.badge100 : mapping.badge70) : '';

              let badgeInfo: BadgeInfo | null = null;
              if (badgeId) {
                try {
                  badgeInfo = await getBadgeById(badgeId);
                } catch (err) {
                  console.error(`[Checkpoint ${cp}]  Erro ao carregar badge:`, err);
                }
              } else {
                console.warn(`[Checkpoint ${cp}]  badgeId vazio`);
              }

              try {
                document.dispatchEvent(new Event('reload:user'));
                showModalRef.current(
                  `checkpoint-${cp}`,
                  <CheckpointRewardModal
                    checkpoint={cp}
                    badge={badgeInfo}
                    rewardsConfig={rewardsConfig}
                    courseID={courseID}
                    ticketType={is100 ? 'gold' : 'silver'}
                  />,
                  'checkpoint-modal checkpoint-modal-priority',
                );

              } catch (err) {
                console.error(`[Checkpoint ${cp}]  Erro ao exibir modal:`, err);
              }

            } catch (err) {
              console.error(`[Checkpoint ${cp}]  Erro geral no processamento:`, err);
            }
          }
        })();
      }
    }

    prevCoursePercent.current = curr;
  }, [coursePercent, user?._id, courseID, courseBadgeMappings, rewardsConfig, showModal]);

  if (folderID !== prevFolderID) {
    setPrevFolderID(folderID);
    setLessonContent(undefined);
    setBreadcrumbs(undefined);
    setLoading(true);
  }

  const getFolderPercentages = useMemo(() => {
    if (Array.isArray(allProgress) && allProgress.length && !!folderID) {
      const folderPercentage = findProgressByID(allProgress, folderID);

      if (!!folderPercentage) {
        return folderPercentage;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [allProgress, folderID]);

  const lesson = useMemo(() => {
    if (lessonContent?.length) {
      const content = lessonContent.find(x => x._id === contentID);

      if (content) {
        return content;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [contentID, lessonContent]);

  const getLikes = useCallback(async () => {
    setLoadingLikes(true);
    const _likes = await getLessonLikes(user?._id, contentID);
    setLikes(_likes);
    setLoadingLikes(false);
  }, [contentID, user]);

  useEffect(() => {
    if (folderID && contentID && !lessonContent) {
      setLoading(true);

      getDetailContent(folderID, false)
        .then(async data => {
          if (data?.length) {
            const breadcrumbs = await getBreadcrumb(folderID);

            if (breadcrumbs?.length) {
              setBreadcrumbs(breadcrumbs);
            }

            setLessonContent(data);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [contentID, folderID, lessonContent]);

  useEffect(() => {
    getLikes();

    if (!!lesson?.type.match(/(quiz|game)/gi)) {
      setShowHeader(false);
    } else {
      setShowHeader(true);
    }

    return () => {
      setShowHeader(true);
    };
  }, [getLikes, lesson, setShowHeader]);

  const handleLike: (value: string) => void = (actionID: string) => {
    if (loadingLikes) return null;

    setLoadingLikes(true);

    setLikes(prevstate => {
      if (typeof prevstate === 'object') {
        if (actionID === 'like_content') {
          return {
            ...(prevstate as LessonLikesInterface),
            playerLiked: 1,
            playerDisliked: false,
          };
        } else {
          return {
            ...(prevstate as LessonLikesInterface),
            playerLiked: false,
            playerDisliked: 1,
          };
        }
      }

      return prevstate;
    });

    actionLog(
      actionID,
      {
        contentId: contentID,
      },
      (likes?.playerLogObj as LessonLikesPlayerObj)?._id,
    )
      .then(() => {
        getLikes();
      })
      .finally(() => setLoadingLikes(false));
  };

  const registerLog = (contentId: string) => {
    registerFolderLog(user?._id, contentId, 'done').finally(() => {
      getFullProgress();
    });
  };

  const nextLessons: DetailContentInterface[] = useMemo((): DetailContentInterface[] => {
    if (!!lessonContent?.length && !!lesson && !!getFolderPercentages) {
      const obj: DetailContentInterface[] = [];

      lessonContent
        .sort((a, b) => a.position - b.position)
        .forEach((item, index) => {
          const lessonActive = item._id === lesson._id;
          const itemPercent = getFolderPercentages.items?.find(x => x._id === item._id);
          let lessonOpen: boolean;

          if (!!itemPercent && itemPercent.percent > 0) {
            lessonOpen = true;
          } else if (lessonActive) {
            lessonOpen = true;
          } else if (
            typeof getFolderPercentages.items !== 'undefined' &&
            getFolderPercentages.items[index - 1]?.percent === 100
          ) {
            lessonOpen = true;
          } else {
            lessonOpen = false;
          }

          obj.push({
            ...item,
            open: true,
            active: lessonActive,
            percent: itemPercent ? itemPercent.percent : 0,
          });
        });

      return obj;
    } else {
      return [];
    }
  }, [getFolderPercentages, lesson, lessonContent]);

  const allLessonsFlat = useMemo(() => {
    if (!courseID || !Array.isArray(allProgress) || !allProgress.length) return [];
    const courseProgress = allProgress.find(x => x._id === courseID);
    if (!courseProgress) return [];
    return getAllLessonsFlat(courseProgress);
  }, [allProgress, courseID]);

  const currentFlatIndex = useMemo(() => {
    return allLessonsFlat.findIndex(l => l.contentID === contentID);
  }, [allLessonsFlat, contentID]);

  const prevLesson = useMemo(() => {
    if (currentFlatIndex > 0) return allLessonsFlat[currentFlatIndex - 1];
    return null;
  }, [currentFlatIndex, allLessonsFlat]);

  const nextLesson = useMemo(() => {
    if (currentFlatIndex >= 0 && currentFlatIndex < allLessonsFlat.length - 1)
      return allLessonsFlat[currentFlatIndex + 1];
    return null;
  }, [currentFlatIndex, allLessonsFlat]);

  const mountBreadcrumb = () => {
    if (breadcrumbs && breadcrumbs.length > 1) {
      return (
        <>
          {breadcrumbs.map((item, index) => (
            <span key={index}>
              {index === 0 && (
                <a className="hover:underline" href={`/cursos/${item._id}`}>
                  {item.title}
                </a>
              )}
              {index > 0 && index + 1 < breadcrumbs.length && (
                <a className="hover:underline" href={`/cursos/${courseID}/${folderID}/${contentID}`}>
                  {item.title}
                </a>
              )}
              {index > 0 && index + 1 === breadcrumbs.length && `${item.title}`}
              {index < breadcrumbs.length - 1 && <> • </>}
            </span>
          ))}
        </>
      );
    } else {
      return <></>;
    }
  };

  const getDocIcon = (extension: string) => {
    if (!!extension.match(/pdf/gi)) {
      return <AiFillFilePdf />;
    } else if (!!extension.match(/xls/gi)) {
      return <AiFillFileExcel />;
    } else if (!!extension.match(/csv/gi)) {
      return <FaFileCsv />;
    } else if (!!extension.match(/(zip|rar|tar|gz)/gi)) {
      return <AiFillFileZip />;
    } else if (!!extension.match(/doc/gi)) {
      return <AiFillFileWord />;
    } else if (!!extension.match(/ppt/gi)) {
      return <FaFilePowerpoint />;
    } else if (!!extension.match(/(png|jpg|jpeg|gif|bmp|webp)/gi)) {
      return <AiFillFileImage />;
    } else if (!!extension.match(/(mkv|mp4|avi|webm|wmv|mov)/gi)) {
      return <FaFileVideo />;
    } else if (!!extension.match(/(mp3|wav|wma|ogg|aac|flac)/gi)) {
      return <FaFileAudio />;
    } else {
      return <FaFileDownload />;
    }
  };

  useEffect(() => {
    if (lesson && lesson.data.showOnScreen) {
      const url = lesson.data.url;
      if (typeof url === 'object' && url && 'url' in url && typeof url.url === 'string') {
        const timerId = setTimeout(() => {
          registerLog(lesson._id);
        }, 60000);

        return () => clearTimeout(timerId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson]);

  function verifyDocument(lesson: any) {
    if (typeof lesson.data.url.url === 'string') {
      return lesson.data.url.url;
    }
  }

  if (!contentID && Array.isArray(allProgress) && allProgress.length) {
    if (getFolderPercentages?.items?.length) {
      return <Navigate to={`/cursos/${courseID}/${folderID}/${getFolderPercentages.items[0]._id}`} replace />;
    } else {
      return <Navigate to={`/erro-404`} replace />;
    }
  }

  if (loading) {
    return (
      <>
        <div className="flex flex-col space-y-6">
          <div className="flex flex-row space-x-3">
            <Placeholder className="w-10 h-10 rounded-full" />
            <Placeholder className="flex-1 h-10 rounded-full" />
          </div>

          <Placeholder className="h-64" />

          <div className="grid grid-cols-3 h-48 gap-6">
            <Placeholder />
            <Placeholder />
            <Placeholder />
          </div>
        </div>
      </>
    );
  }

  if (!lesson || !breadcrumbs?.length) {
    return <Navigate to={`/erro-404`} replace />;
  }

  const isQuizOrGame = !!lesson.type.match(/(quiz|game)/gi);

  return (
    <>
      <div className={`min-h-screen grid gap-8 items-start ${
  !isSidebarCollapsed && !!nextLessons?.length && !!courseID && !!folderID && !isQuizOrGame
    ? 'grid-cols-1 lg:grid-cols-[3fr_1fr]'
    : !!nextLessons?.length && !!courseID && !!folderID && !isQuizOrGame
      ? 'grid-cols-1 lg:grid-cols-[1fr_auto]'  
      : 'grid-cols-1'
}`}>
        <section className="flex flex-1 w-full">
          <div className={`flex-1 min-w-0 ${isQuizOrGame ? 'w-full flex flex-col' : ''}`}>
            {/* Breadcrumbs */}
            <div className="mb-4">
              {isQuizOrGame ? (
                <div className="flex items-center justify-between">
                  <div>
                    {!!breadcrumbs[0].title && (
                      <p className="text-sm lg:text-base font-bold text-purple leading-snug inline">
                        {mountBreadcrumb()}
                      </p>
                    )}
                    {!!lesson.title && (
                      <h1 className="text-lg lg:text-2xl font-medium text-[#181b1a] mt-1">{lesson.title}</h1>
                    )}
                  </div>
                  <button
                    className="flex items-center gap-2 text-sm font-medium text-[#181b1a] hover:opacity-75 transition-opacity"
                    onClick={() => navigate(-1)}
                  >
                    Fechar
                    <span className="w-8 h-8 bg-orange rounded-lg flex items-center justify-center">
                      <FaTimes className="text-white text-xs" />
                    </span>
                  </button>
                </div>
              ) : (
                <>
                  {!!breadcrumbs[0].title && (
                    <p className="text-xs lg:text-xl font-bold text-purple leading-snug uppercase tracking-wide">
                      {mountBreadcrumb()}
                    </p>
                  )}
                </>
              )}
            </div>

            {!isQuizOrGame && (
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-xl lg:text-2xl font-medium text-[#181b1a] flex-1 min-w-0 mr-4">
                  {lesson.title}
                </h1>

                <div className="flex items-center gap-3 flex-shrink-0 mt-1">
                  <button
                    className={`flex items-center gap-1 text-lg transition-colors outline-none
                      ${!!likes?.playerLiked ? 'text-[#6fd181]' : 'text-gray-300 hover:text-[#6fd181]'}`}
                    onClick={() => (!likes?.playerLiked ? handleLike('like_content') : null)}
                    disabled={!!likes?.playerLiked}
                    title="Gostei"
                  >
                    <AiOutlineLike className="w-7 h-auto" />
                    {!!likes?.qtdLikes && <span className="text-sm">{likes.qtdLikes}</span>}
                  </button>

                  <button
                    className={`flex items-center gap-1 text-lg transition-colors outline-none
                      ${!!likes?.playerDisliked ? 'text-[#ed4956]' : 'text-gray-300 hover:text-[#ed4956]'}`}
                    onClick={() => (!likes?.playerDisliked ? handleLike('dislike_content') : null)}
                    disabled={!!likes?.playerDisliked}
                    title="Não gostei"
                  >
                    <AiOutlineDislike className="w-7 h-7" />
                  </button>
                </div>
              </div>
            )}

            {!!lesson.data?.intro && !!lesson.type.match(/(video|doc)/gi) && (
              <p className="text-sm lg:text-base font-medium text-[#181b1a] mb-4 leading-snug">{lesson.data.intro}</p>
            )}
            

            {!!lesson.data?.description && lesson.type !== 'text' && lesson.type !== 'video' && (
              <p className="text-base text-[#181b1a] mb-4 text-left" dangerouslySetInnerHTML={{ __html: lesson.data.description }} />
            )}

            {lesson.type === 'video' && (
              <div className="mb-5">
                <Video
                  content={lesson}
                  prevLesson={!!prevLesson}
                  nextLesson={!!nextLesson}
                  onPrev={() => prevLesson && navigate(`/cursos/${courseID}/${prevLesson.folderID}/${prevLesson.contentID}`)}
                  onNext={() => nextLesson && navigate(`/cursos/${courseID}/${nextLesson.folderID}/${nextLesson.contentID}`)}
                />
              </div>
            )}

            {/* Content - Audio */}
            {lesson.type === 'audio' && (
              <>
                <Audio content={lesson} />
                <LessonNavigation
                  hasPrev={!!prevLesson}
                  hasNext={!!nextLesson}
                  onPrev={() => prevLesson && navigate(`/cursos/${courseID}/${prevLesson.folderID}/${prevLesson.contentID}`)}
                  onNext={() => nextLesson && navigate(`/cursos/${courseID}/${nextLesson.folderID}/${nextLesson.contentID}`)}
                />
              </>
            )}

            {/* Content - Text */}
            {lesson.type === 'text' && (
              <>
                <Text content={lesson} />
                <LessonNavigation
                  hasPrev={!!prevLesson}
                  hasNext={!!nextLesson}
                  onPrev={() => prevLesson && navigate(`/cursos/${courseID}/${prevLesson.folderID}/${prevLesson.contentID}`)}
                  onNext={() => nextLesson && navigate(`/cursos/${courseID}/${nextLesson.folderID}/${nextLesson.contentID}`)}
                />
              </>
            )}

            {/* Content - Quiz */}
            {lesson.type === 'quiz' && (
              <>
                <Quiz content={lesson} />
                <LessonNavigation
                  hasPrev={!!prevLesson}
                  hasNext={!!nextLesson}
                  onPrev={() => prevLesson && navigate(`/cursos/${courseID}/${prevLesson.folderID}/${prevLesson.contentID}`)}
                  onNext={() => nextLesson && navigate(`/cursos/${courseID}/${nextLesson.folderID}/${nextLesson.contentID}`)}
                />
              </>
            )}

            {/* Content - Game */}
            {lesson.type.match(/game_*/gi) && (
              <>
                <GameContainer id={GameFolder[lesson.type]} contentId={contentID} className="mb-5" />
                <LessonNavigation
                  hasPrev={!!prevLesson}
                  hasNext={!!nextLesson}
                  onPrev={() => prevLesson && navigate(`/cursos/${courseID}/${prevLesson.folderID}/${prevLesson.contentID}`)}
                  onNext={() => nextLesson && navigate(`/cursos/${courseID}/${nextLesson.folderID}/${nextLesson.contentID}`)}
                />
              </>
            )}

            {/* Content - Document */}
            {lesson.type === 'doc' && (
              <div>
                {lesson.data.showOnScreen ? (
                  <>
                    <embed src={verifyDocument(lesson)} type="" className="w-full min-h-[60vh] rounded-xl" id="embed" />
                    <LessonNavigation
                      hasPrev={!!prevLesson}
                      hasNext={!!nextLesson}
                      onPrev={() => prevLesson && navigate(`/cursos/${courseID}/${prevLesson.folderID}/${prevLesson.contentID}`)}
                      onNext={() => nextLesson && navigate(`/cursos/${courseID}/${nextLesson.folderID}/${nextLesson.contentID}`)}
                    />
                  </>
                ) : (
                  <div className="flex items-center justify-center pt-8">
                    <a
                      href={(lesson.data.url as File).url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex flex-col items-center group"
                      download
                      onClick={() => registerLog(lesson._id)}
                    >
                      <span className="w-32 h-32 rounded-2xl bg-orange/5 flex items-center justify-center mb-3">
                        <span className="text-orange text-5xl">{getDocIcon((lesson.data.url as File).extension)}</span>
                      </span>
                      <span className="text-base font-medium text-[#181b1a] group-hover:text-purple transition-colors">
                        Baixar arquivo
                      </span>
                      <span className="text-sm text-gray-400">
                        {(lesson.data.url as File).extension?.replace(/\./gi, '').toUpperCase()} •{' '}
                        {formatBytes((lesson.data.url as File).size)}
                      </span>
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
          {!!nextLessons?.length && !!courseID && !!folderID && !isQuizOrGame && (
            <div 
              className={`hidden lg:block sticky top-0 ${isSidebarCollapsed ? 'w-auto' : ''}`}
              style={{ marginTop: breadcrumbs?.[0]?.title ? '-3.7rem' : '0' }}
            >
              <NextLessons
                nextLessons={nextLessons}
                courseID={courseID}
                folderID={folderID}
                allProgress={allProgress}
                rewardsConfig={rewardsConfig}
                breadcrumbs={breadcrumbs}
                onCollapsedChange={setIsSidebarCollapsed}
              />
            </div>
          )}
      </div>    

      {/* Mobile: Course Content below main content */}
      {!!nextLessons?.length && !!courseID && !!folderID && !isQuizOrGame && (
        <div className="lg:hidden mt-6 border-t border-gray-100 pt-4">
          <NextLessons
            nextLessons={nextLessons}
            courseID={courseID}
            folderID={folderID}
            allProgress={allProgress}
            rewardsConfig={rewardsConfig}
            breadcrumbs={breadcrumbs}
          />
        </div>
      )}
    </>
  );
}
